import { useCallback } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import {
    addStepAfter,
    deleteStepFromPlan,
    selectStep,
    selectAdjacentStep,
    navigateToInstructions,
} from '../automationStudioNewSlice';
import { selectInstructions, selectSelectedStep } from '../automationStudioNewSelectors';
import { buildCustomStep } from '../instructionMutations';
import { copyStepsAction } from '../../../utils/automation/copySteps';
import { pasteStepsNew } from '../pasteStepsNew';
import { readSteps as clipboardReadSteps } from '../../../utils/automation/clipboardService';
import { showSnackbar } from '../../snakbar/snackbarSlice';
import { TOASTS } from '../../../utils/automation/copyPasteConstants';

export default function useAutomationStudioNewStepHandlers({ testCaseId }) {
    const dispatch = useDispatch();
    const store = useStore();
    const instructions = useSelector(selectInstructions);
    const selectedStep = useSelector(selectSelectedStep);

    const getCopyPasteState = useCallback(
        () => {
            const s = store.getState().automationStudioNew;
            return {
                instructions: s.planDraft.instructions,
                selectedStepIds: s.selection.selectedStepIds,
                testCaseId: testCaseId || s.savedPlan.testCaseId,
            };
        },
        [store, testCaseId]
    );

    const handleAddStep = useCallback(
        (instructionId, afterStepIndex, description) => {
            const inst = instructions.find((i) => i.id === instructionId);
            if (!inst?.aiSteps?.length) return;
            const step = buildCustomStep({
                instructionId,
                instructionText: inst.text,
                description: description ?? '',
            });
            dispatch(addStepAfter({ instructionId, afterStepIndex, step }));
        },
        [dispatch, instructions]
    );

    const handleDeleteStep = useCallback(
        (stepId) => {
            if (!stepId) return;
            dispatch(deleteStepFromPlan(stepId));
            dispatch(navigateToInstructions());
        },
        [dispatch]
    );

    const handlePrevStep = useCallback(() => {
        dispatch(selectAdjacentStep('prev'));
    }, [dispatch]);

    const handleNextStep = useCallback(() => {
        dispatch(selectAdjacentStep('next'));
    }, [dispatch]);

    const handleCopy = useCallback(
        async (stepId) => {
            if (stepId) dispatch(selectStep(stepId));
            await copyStepsAction({ getState: getCopyPasteState, dispatch: store.dispatch });
        },
        [dispatch, getCopyPasteState, store]
    );

    const runPaste = useCallback(
        async (position, anchorStepId) => {
            const env = await clipboardReadSteps();
            if (!env) {
                store.dispatch(
                    showSnackbar({ message: TOASTS.PASTE_UNRECOGNIZED, severity: 'warning', duration: 4000 })
                );
                return;
            }
            pasteStepsNew({
                envelope: env,
                position,
                anchorStepId,
                getState: () => ({
                    instructions: store.getState().automationStudioNew.planDraft.instructions,
                }),
                dispatch: store.dispatch,
            });
        },
        [store]
    );

    const handlePasteAbove = useCallback(
        async (stepId) => {
            const anchor = stepId || store.getState().automationStudioNew.selection.selectionAnchorId;
            if (!anchor) {
                store.dispatch(
                    showSnackbar({ message: TOASTS.PASTE_ABOVE_NEEDS_ANCHOR, severity: 'info', duration: 4000 })
                );
                return;
            }
            await runPaste('above', anchor);
        },
        [runPaste, store]
    );

    const handlePasteBelow = useCallback(
        async (stepId) => {
            const anchor = stepId || store.getState().automationStudioNew.selection.selectionAnchorId;
            await runPaste(anchor ? 'below' : 'append-end', anchor);
        },
        [runPaste, store]
    );

    return {
        selectedStep,
        handleAddStep,
        handleDeleteStep,
        handlePrevStep,
        handleNextStep,
        handleCopy,
        handlePasteAbove,
        handlePasteBelow,
        getCopyPasteState,
    };
}
